import React, { useCallback, useEffect, useState } from "react";
import { getNpdesPermits, addNpdesPermit, updateNpdesPermit, archiveNpdesPermit } from "../api/client";

/**
 * The POTW's own NPDES permits, one line per five-year term. A renewal is added
 * as a new line and the old one stays as history; each report uses the permit in
 * force during its period. Part of the POTW Profile.
 */
type Permit = { id: number; permit_number: string; effective_date: string; expiration_date: string; notes: string | null };
type Alert = { level: string; message: string } | null;
const EMPTY = { permit_number: "", effective_date: "", expiration_date: "", notes: "" };

const us = (iso: string) => { const [y, m, d] = iso.split("-"); return `${m}/${d}/${y}`; };

export default function NpdesPermits({ canEdit = true }: { canEdit?: boolean }) {
  const [permits, setPermits] = useState<Permit[]>([]);
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [alert, setAlert] = useState<Alert>(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState<number | null>(null);   // null + showForm = adding
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    getNpdesPermits()
      .then(r => { setPermits(r.data.permits); setCurrentId(r.data.current_id); setAlert(r.data.alert); })
      .catch(() => setError("Couldn't load the NPDES permits."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  const statusOf = (p: Permit) =>
    p.id === currentId ? (p.expiration_date < today ? "Current (expired — continued)" : "Current")
      : p.effective_date > today ? "Upcoming" : "Past";

  const open = (p?: Permit) => {
    setError("");
    setEditingId(p ? p.id : null);
    setForm(p ? { permit_number: p.permit_number, effective_date: p.effective_date,
                  expiration_date: p.expiration_date, notes: p.notes ?? "" } : EMPTY);
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      if (editingId) await updateNpdesPermit(editingId, form);
      else await addNpdesPermit(form);
      setShowForm(false); setEditingId(null); setForm(EMPTY);
      load();
    } catch (e: any) {
      setError(e.response?.data?.error ?? "Couldn't save the permit.");
    } finally {
      setSaving(false);
    }
  };

  const archive = async (p: Permit) => {
    if (!window.confirm(`Remove ${p.permit_number} (${us(p.effective_date)} – ${us(p.expiration_date)}) from the list?\n\n`
      + "Use this only for a line entered by mistake. It is archived, not deleted. "
      + "A renewed permit is added as a new line instead.")) return;
    try { await archiveNpdesPermit(p.id); load(); }
    catch (e: any) { setError(e.response?.data?.error ?? "Couldn't remove the permit."); }
  };

  return (
    <div>
      {alert && (
        <div role="status" style={{ ...st.alert, ...(alert.level === "application_due" ? {} : st.alertRed) }}>
          {alert.message}
        </div>
      )}
      {permits.length === 0 ? (
        <p style={st.muted}>No NPDES permit recorded yet.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
        <table style={st.table}>
          <thead><tr>
            <th style={st.th}>Permit #</th><th style={st.th}>Effective</th><th style={st.th}>Expires</th>
            <th style={st.th}>Status</th>{canEdit && <th style={st.th} />}
          </tr></thead>
          <tbody>
            {permits.map(p => (
              <tr key={p.id} style={p.id === currentId ? { background: "#f0fff4" } : undefined}>
                <td style={st.td}><strong>{p.permit_number}</strong>
                  {p.notes && <div style={st.note}>{p.notes}</div>}</td>
                <td style={st.td}>{us(p.effective_date)}</td>
                <td style={st.td}>{us(p.expiration_date)}</td>
                <td style={st.td}>{statusOf(p)}</td>
                {canEdit && (
                  <td style={{ ...st.td, whiteSpace: "nowrap" }}>
                    <button type="button" style={st.small} onClick={() => open(p)}>Edit</button>{" "}
                    <button type="button" style={st.small} onClick={() => archive(p)}>Remove</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {canEdit && !showForm && (
        <button type="button" style={st.add} onClick={() => open()}>+ Add NPDES permit (renewal)</button>
      )}
      {canEdit && showForm && (
        <div style={st.form}>
          <strong style={{ fontSize: 13 }}>{editingId ? "Edit NPDES permit" : "Add NPDES permit"}</strong>
          <label style={st.label}>Permit number
            <input style={st.input} value={form.permit_number} placeholder="e.g. SC0000000"
              onChange={e => setForm(f => ({ ...f, permit_number: e.target.value }))} />
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <label style={{ ...st.label, flex: 1 }}>Effective date
              <input style={st.input} type="date" value={form.effective_date}
                onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))} />
            </label>
            <label style={{ ...st.label, flex: 1 }}>Expiration date
              <input style={st.input} type="date" value={form.expiration_date}
                onChange={e => setForm(f => ({ ...f, expiration_date: e.target.value }))} />
            </label>
          </div>
          <label style={st.label}>Notes (optional)
            <input style={st.input} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </label>
          {error && <div role="alert" style={st.error}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={st.save} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save permit"}</button>
            <button type="button" style={st.small} onClick={() => { setShowForm(false); setError(""); }}>Cancel</button>
          </div>
        </div>
      )}
      {!showForm && error && <div role="alert" style={st.error}>{error}</div>}
    </div>
  );
}

const st: Record<string, React.CSSProperties> = {
  muted: { color: "#718096", fontSize: 13 },
  alert: { background: "#fffaf0", border: "1px solid #fbd38d", color: "#7b341e", borderRadius: 6,
           padding: "8px 10px", fontSize: 13, marginBottom: 10 },
  alertRed: { background: "#fff5f5", border: "1px solid #fc8181", color: "#9b2c2c" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 10 },
  th: { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e2e8f0", color: "#4a5568" },
  td: { padding: "6px 8px", borderBottom: "1px solid #edf2f7", verticalAlign: "top" },
  note: { fontSize: 11, color: "#718096", fontWeight: 400 },
  small: { padding: "3px 10px", fontSize: 12, background: "#fff", border: "1px solid #cbd5e0", borderRadius: 4,
           cursor: "pointer", color: "#4a5568" },
  add: { padding: "6px 14px", fontSize: 13, background: "#fff", border: "1px solid #2b6cb0", color: "#2b6cb0",
         borderRadius: 5, cursor: "pointer" },
  form: { border: "1px solid #e2e8f0", borderRadius: 6, padding: 12, display: "flex", flexDirection: "column", gap: 8 },
  label: { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, fontWeight: 600, color: "#4a5568" },
  input: { padding: "7px 9px", border: "1px solid #cbd5e0", borderRadius: 5, fontSize: 13 },
  save: { padding: "6px 14px", background: "#2b6cb0", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer" },
  error: { color: "#c53030", fontSize: 13, margin: "4px 0" },
};
